import * as React from 'react';

import { useDispatch } from 'react-redux';
import {
    AchatsAppDispatch,
    ReminderPublisher,
    useAchatsService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { ReminderPublisherApiSendMailReminderPublisherItemRequest } from '@europrocurement/l2d-domain/openApi/ApiAchats';
import { useTheme } from '@mui/material';
import { MessagesTypes } from '@europrocurement/flexy-components/constants/wording/modals';
import { useApiRequestControllable } from '@europrocurement/l2d-modules';
import { useSnackbar } from 'notistack';

export type UseSendMailReminderPublisherProps = {
    from: 'list' | 'details';
};

/**
 * Hook personnalisé pour gérer l'envoi de mail pour un ou plusieurs rappels fournisseurs
 *
 * @param options - Options de configuration du hook
 * @returns Fonctions et état pour gérer l'envoi de mails
 */
const useSendMailReminderPublisher = ({ from }: UseSendMailReminderPublisherProps) => {
    const theme = useTheme();
    const xIdSociete = useSociete();
    const dispatch = useDispatch<AchatsAppDispatch>();
    const { request, requestState } = useApiRequestControllable();
    const { enqueueSnackbar } = useSnackbar();

    const { getReminderPublisher, reminderPublisherApi, selectReminderPublisher } =
        useAchatsService();

    /**
     * Génère les messages de confirmation appropriés en fonction du statut du rappel
     *
     * @param status - Statut actuel du rappel
     * @returns Objets de messages pour la confirmation
     */
    const getMessages = React.useCallback((status: ReminderPublisher['status']): MessagesTypes => {
        if (!status) return {};

        if (status === 'sent') {
            return {
                question: 'Êtes-vous sûr de vouloir envoyer ce rappel à nouveau ?',
                validation: 'Oui, je veux envoyer ce rappel une nouvelle fois',
                annulation: "Non, je veux annuler l'envoi",
            };
        }

        return {
            question: 'Êtes-vous sûr de vouloir envoyer ce rappel ?',
            validation: 'Oui, je veux envoyer ce rappel',
            annulation: "Non, je veux annuler l'envoi",
        };
    }, []);

    /**
     * Génère les messages de confirmation pour l'envoi multiple de rappels
     *
     * @param rappels - Liste des rappels à envoyer
     * @returns Objets de messages pour la confirmation multiple
     */
    const getMultipleMessages = React.useCallback((rappels: ReminderPublisher[]): MessagesTypes => {
        const alreadySentCount = rappels.filter((r) => r.status === 'sent').length;
        const totalCount = rappels.length;

        if (alreadySentCount === 0) {
            return {
                question: `Êtes-vous sûr de vouloir envoyer ces ${totalCount} rappels ?`,
                validation: 'Oui, je veux envoyer ces rappels',
                annulation: "Non, je veux annuler l'envoi",
            };
        }
        if (alreadySentCount === totalCount) {
            return {
                question: `Êtes-vous sûr de vouloir envoyer à nouveau ces ${totalCount} rappels déjà envoyés ?`,
                validation: 'Oui, je veux envoyer ces rappels une nouvelle fois',
                annulation: "Non, je veux annuler l'envoi",
            };
        }
        return {
            question: `Êtes-vous sûr de vouloir envoyer ces ${totalCount} rappels (dont ${alreadySentCount} déjà envoyés) ?`,
            validation: 'Oui, je veux envoyer ces rappels',
            annulation: "Non, je veux annuler l'envoi",
        };
    }, []);

    /**
     * Envoie un mail pour un rappel fournisseur spécifique
     *
     * @param reminderPublisher - Le rappel fournisseur à envoyer
     */
    const sendMailReminderPublisher = React.useCallback(
        (reminderPublisher: ReminderPublisher) => {
            if (!reminderPublisher?.id) return;

            const requestParameters: ReminderPublisherApiSendMailReminderPublisherItemRequest = {
                id: reminderPublisher?.id,
                reminderPublisherJsonldReminderPublisherWriteTimestampableBlameambleGroupSocieteAwareGroup:
                    {
                        fournisseurId: reminderPublisher.fournisseurId,
                        customMessage: reminderPublisher.customMessage,
                    },
                xIdSociete,
            };

            request(() => reminderPublisherApi.sendMailReminderPublisherItem(requestParameters), {
                successMessage: 'Envoi du rappel fournisseur avec succès.',
                errorMessage: "Une erreur est survenue lors de l'envoi du rappel fournisseur.",
                successCallback() {
                    if (from === 'details') {
                        if (reminderPublisher.id) {
                            dispatch(selectReminderPublisher({ id: reminderPublisher.id }));
                        }
                    } else {
                        dispatch(getReminderPublisher({}));
                    }
                },
                confirmationPromise: true,
                confirmationPromiseProps: {
                    color: theme.palette.primary,
                    messages: getMessages(reminderPublisher.status),
                    loadingMessage: 'Envoi du rappel en cours...',
                },
            });
        },
        [
            dispatch,
            from,
            getMessages,
            getReminderPublisher,
            reminderPublisherApi,
            request,
            selectReminderPublisher,
            theme.palette.primary,
            xIdSociete,
        ],
    );
    /**
     * Envoie un mail pour plusieurs rappels fournisseurs simultanément
     *
     * @param reminderPublishers - Tableau de rappels fournisseurs à envoyer
     */
    const sendMultipleMailReminderPublisher = React.useCallback(
        async (reminderPublishers: ReminderPublisher[]) => {
            if (reminderPublishers.length === 0) return;

            // Filtrer les rappels valides (avec ID)
            const validReminderPublishers = reminderPublishers.filter((rp) => !!rp.id);

            if (validReminderPublishers.length === 0) {
                enqueueSnackbar('Aucun rappel valide à envoyer', { variant: 'warning' });
                return;
            }

            // Demander confirmation à l'utilisateur
            try {
                // Utiliser une requête API réelle mais inoffensive pour obtenir la confirmation
                // Nous utiliserons une requête GET sur un rappel existant pour satisfaire le typage Axios
                const firstReminderId = validReminderPublishers[0].id;

                await request(
                    () =>
                        reminderPublisherApi.getReminderPublisherItem({
                            id: firstReminderId!,
                            xIdSociete,
                        }),
                    {
                        confirmationPromise: true,
                        withToaster: false, // Ne pas afficher de toast pour cette requête de confirmation
                        confirmationPromiseProps: {
                            color: theme.palette.primary,
                            messages: getMultipleMessages(validReminderPublishers),
                            loadingMessage: "Préparation de l'envoi multiple...",
                        },
                    },
                );

                // Si l'utilisateur confirme, traiter l'envoi en masse
                enqueueSnackbar(
                    `Début de l'envoi de ${validReminderPublishers.length} rappels...`,
                    { variant: 'info' },
                );

                // Créer un tableau de promesses d'envoi
                const sendPromises = validReminderPublishers.map(async (reminder) => {
                    try {
                        if (!reminder.id) return false;

                        const requestParameters: ReminderPublisherApiSendMailReminderPublisherItemRequest =
                            {
                                id: reminder.id,
                                reminderPublisherJsonldReminderPublisherWriteTimestampableBlameambleGroupSocieteAwareGroup:
                                    {
                                        fournisseurId: reminder.fournisseurId,
                                        customMessage: reminder.customMessage,
                                    },
                                xIdSociete,
                            };

                        await reminderPublisherApi.sendMailReminderPublisherItem(requestParameters);
                        return true;
                    } catch (error) {
                        console.error(`Erreur lors de l'envoi du rappel ${reminder.id}:`, error);
                        return false;
                    }
                });

                // Exécuter les envois et attendre les résultats
                const results = await Promise.all(sendPromises);

                // Compter les succès et échecs
                const successCount = results.filter(Boolean).length;
                const failureCount = validReminderPublishers.length - successCount;

                // Afficher un message récapitulatif
                if (failureCount === 0) {
                    enqueueSnackbar(
                        `Tous les rappels (${successCount}) ont été envoyés avec succès.`,
                        { variant: 'success' },
                    );
                } else if (successCount === 0) {
                    enqueueSnackbar(`Échec de l'envoi de tous les rappels.`, { variant: 'error' });
                } else {
                    enqueueSnackbar(
                        `${successCount} rappels envoyés avec succès, ${failureCount} échecs.`,
                        { variant: 'warning' },
                    );
                }

                // Rafraîchir les données
                dispatch(getReminderPublisher({}));
            } catch (error) {
                // L'utilisateur a annulé ou une erreur est survenue au niveau de la confirmation
                console.error('Opération multiple annulée ou erreur:', error);
            }
        },
        [
            dispatch,
            enqueueSnackbar,
            getMultipleMessages,
            getReminderPublisher,
            reminderPublisherApi,
            request,
            theme.palette.primary,
            xIdSociete,
        ],
    );

    return {
        sendMailReminderPublisher,
        sendMultipleMailReminderPublisher,
        state: requestState,
    };
};

export default useSendMailReminderPublisher;
